import ObjectPreview from "@components/objects/ObjectPreview";
import { Typography } from "@mui/joy";
import { formatDate, formatTime } from "@utils/dates";
import { generateAddress } from "@utils/generateAddress";
import { ensureNumber, formatCurrency, formatNumber, isNumeric } from "@utils/numbers";
import { endpointForMorphType } from "@utils/morphTypes";
import { relationshipRoles } from "@utils/selectOptions";
import { yesNo } from "@utils/yesNo";

type BaseColumn = {
    label: string;
    column: string;
    width?: string;
}

type PrimaryRelationshipColumn = /*BaseColumn &*/ {
    label?: string;
    column?: string;
    endpoint: string;
}

type TextColumn = BaseColumn & {
    secondaryColumn?: string;
}

type RenderFn = {
    object: Function;
}
type RenderResponse = {
    show: RenderFn;
    edit: RenderFn;
    editDisabled?: RenderFn;
}

type ColumnDeclaration = {
    label?: string;
    render: RenderResponse;
};

export const getColumnValue = (object, column) => {
    let objectRef = ~column.indexOf('pivot.') ? object.pivot : object;
    column = ~column.indexOf('pivot.') ? column.split('pivot.')[1] : column;
    return objectRef[column];
}

/*
|--------------------------------------------------------------------------
| General Column Types
|--------------------------------------------------------------------------
*/
export const genericColumn = ({ label, /*column,*/ render, ...restProps } = {}) => {
    return {
        label,
        ...restProps,
        render/*: {
            show: (object) => {
                return <>
                    {getColumnValue(object, column)}
                </>
            },
        },
        */
    }
};

export const primaryRelationshipColumn = ({ label = 'Name', column = 'name', endpoint, ...restProps }:PrimaryRelationshipColumn):ColumnDeclaration => ({
    label,
    column,
    type: 'relationship',
    endpoint,
    editableAfterSave: false,
    render: {
        show: (object, parentObject = null) => {
            return <>
                <ObjectPreview key={object.id} to={`/${endpoint}/${object.id}`} object={object}>
                    <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{object[column]}</Typography>
                </ObjectPreview>
            </>
        },
        editDisabled: (object) => {
            return <>{object?.[column] ?? 'N/A - please implement'}</>
        },
    },
    ...restProps,
});

export const relationshipColumn = ({ label = 'Name', column = 'name', endpoint, ...restProps }:PrimaryRelationshipColumn):ColumnDeclaration => ({
    label,
    column,
    type: 'relationship',
    endpoint,
    editableAfterSave: false,
    render: {
        show: (object, parentObject = null) => {
            return <>
                {object[column]}
            </>
        },
        editDisabled: (object) => {
            return <>{object?.[column] ?? 'N/A - please implement'}</>
        },
    },
    ...restProps,
});

export const polymorphicColumn = ({ label = 'Name', column, typeColumn, relationship, endpoints, displayColumn = 'name', ...restProps }):ColumnDeclaration => ({
    label,
    column,
    typeColumn,
    relationship,
    endpoints,
    type: 'polymorphic',
    editableAfterSave: true,
    render: {
        show: (object) => {
            const related = object?.[relationship];

            if(!related) {
                return <></>;
            }

            const relatedEndpoint = endpointForMorphType(object?.[typeColumn]);
            const display = related?.[displayColumn] ?? related?.name ?? related?.address ?? '';

            if(!relatedEndpoint) {
                return <Typography fontSize={'md'}>{display}</Typography>;
            }

            return <ObjectPreview key={related.id} to={`/${relatedEndpoint}/${related.id}`} object={related}>
                <Typography sx={{display: 'inline-block'}} fontSize={'md'}>{display}</Typography>
            </ObjectPreview>
        },
        editDisabled: (object) => {
            const related = object?.[relationship];
            return <>{related?.[displayColumn] ?? related?.name ?? related?.address ?? ''}</>
        },
    },
    ...restProps,
});

export const selectColumn = ({ label = 'Name', column = 'name', options, ...restProps }:PrimaryRelationshipColumn):ColumnDeclaration => ({
    label,
    column,
    type: 'select',
    options,
    editableAfterSave: true,
    render: {
        show: (object, parentObject = null) => {
            return <>
                {object[column]}
            </>
        },
        editDisabled: (object) => {
            return <>{object?.[column] ?? 'N/A - please implement'}</>
        },
    },
    ...restProps,
});

export const tagColumn = ({ label = 'Name', column = 'name', endpoint, ...restProps }:PrimaryRelationshipColumn):ColumnDeclaration => ({
    label,
    column,
    type: 'tag',
    endpoint,
    editableAfterSave: false,
    render: {
        show: (object, parentObject = null) => {
            return <>
                {object[column]}
            </>
        },
        editDisabled: (object) => {
            return <>{object?.[column] ?? 'N/A - please implement'}</>
        },
    },
    ...restProps,
});

export const textColumn = ({ label, column, secondaryColumn = undefined, ...restProps }:TextColumn) => {
    return {
        label,
        column,
        ...restProps,
        render: {
            show: (object) => {
                let renderValue = getColumnValue(object, column);
                if(restProps?.format) {
                    if(restProps.format === 'number' && isNumeric(renderValue)) {
                        renderValue = formatNumber(ensureNumber(renderValue));
                    }
                }
                // restProps
                const secondary = secondaryColumn ? getColumnValue(object, secondaryColumn) : null;
                return <>
                    <Typography fontSize={'md'}>{renderValue}</Typography>
                    {secondary && <Typography fontSize={'sm'}>{secondary}</Typography>}
                </>
            },
        },
    }
};

export const textareaColumn = ({ label, column, secondaryColumn = undefined, ...restProps }:TextColumn) => {
    return {
        label,
        column,
        type: 'textarea',
        ...restProps,
        render: {
            show: (object) => {
                const secondary = secondaryColumn ? getColumnValue(object, secondaryColumn) : null;
                return <>
                    <Typography fontSize={'md'}>{getColumnValue(object, column)}</Typography>
                    {secondary && <Typography fontSize={'sm'}>{secondary}</Typography>}
                </>
            },
        },
    }
};

export const yesNoColumn = ({ label, column, pivot = false, ...restProps } = {}) => ({
    label,
    type: 'boolean',
    column,
    ...restProps,
    render: {
        show: (object) => {
            return <Typography fontSize={'md'}>
                {yesNo(!!(getColumnValue(object, column)))}
            </Typography>
        },
    },
});

export const dateColumn = ({ label = 'Date', column = 'date', className = null, ...restProps } = {}) => ({
    label,
    type: 'date',
    column,
    class: className,
    ...restProps,
    render: {
        show: (object) => {
            return <Typography fontSize={'md'}>
                {formatDate(object[column])}
            </Typography>
        },
        /*
        edit: (object, parentObject) => {
            return <>date column</>
        }
        */
    },
});
export const timeColumn = ({ label = 'Time', column = 'time', className = null, ...restProps } = {}) => ({
    label,
    type: 'time',
    column,
    class: className,
    ...restProps,
    render: {
        show: (object) => {
            return <Typography fontSize={'md'}>
                {formatTime(object[column])}
            </Typography>
        },
        /*
        edit: (object, parentObject) => {
            return <>date column</>
        }
        */
    },
});

export const currencyColumn = ({ label = 'Amount', column = 'amount', ...restProps } = {}) => ({
    label,
    column,
    startDecorator: "$",
    inputType: 'number',
    ...restProps,
    render: {
        show: (object) => {
            return <Typography fontSize={'md'}>
                {formatCurrency(object[column])}
            </Typography>
        },
    },
});

export const numberColumn = ({ label, column, ...restProps } = {}) => ({
    label,
    column,
    ...restProps,
    render: {
        show: (object) => {
            return <Typography fontSize={'md'}>
                {formatNumber(object[column])}
            </Typography>
        },
    },
});

/*
|--------------------------------------------------------------------------
| Common Columns
|--------------------------------------------------------------------------
*/
export const addressColumn = genericColumn({
    label: 'Address',
    column: 'address',
    type: 'relationship',
    endpoint: 'properties',
    editableAfterSave: false,
    render: {
        show: (object) => {
            const secondaryAddress = generateAddress(object, true);
            return <>
                <ObjectPreview to={`/properties/${object.id}`} object={object}>
                    <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{object.address}</Typography>
                </ObjectPreview>
                {secondaryAddress && (<><br/><span dangerouslySetInnerHTML={{__html: secondaryAddress}}></span></>)}
            </>
        },
        editDisabled: (object) => {
            const address = generateAddress(object, false);
            return <>
                {/* <ObjectPreview to={`/properties/${object.id}`} object={object}>{object.address}</ObjectPreview> */}
                {address && (<><span dangerouslySetInnerHTML={{__html: address}}></span></>)}
            </>
        }
    },
});

export const roleColumn = genericColumn({
    label: 'Role',
    type: 'select',
    column: 'pivot.value',
    options: relationshipRoles,
    render: {
        show: textColumn({label: 'Role', column: 'pivot.value'}).render.show,
        // edit:
    },
})
/*
genericColumn({
    label: 'Role',
    render: {
        show: (object) => {
            return object.pivot.value;
        },
    },
});
*/
